import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebaseConfig';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { 
  IoArrowBack, IoPawOutline, IoPaw, IoTrashOutline, 
  IoPencilOutline, IoAddCircleOutline, IoClose, IoCloudUploadOutline,
  IoFemale, IoMale, IoAlertCircleOutline
} from 'react-icons/io5';
import './MisMascotasScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';

function MisMascotasScreen() {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  
  // Data list and loading
  const [mascotas, setMascotas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal control & form states
  const [showModal, setShowModal] = useState(false);
  const [editingPetId, setEditingPetId] = useState(null);
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState('Perro');
  const [raza, setRaza] = useState('');
  const [genero, setGenero] = useState('Macho');
  const [edad, setEdad] = useState('');
  const [caracteristicas, setCaracteristicas] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [modalError, setModalError] = useState('');

  // 1. Fetch User's Pets
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "mascotas"),
      where("usuarioId", "==", currentUser.uid)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by date client-side
      list.sort((a, b) => {
        const tA = a.fechaRegistro?.seconds || 0;
        const tB = b.fechaRegistro?.seconds || 0;
        return tB - tA;
      });
      setMascotas(list);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching pets:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Open modal for adding
  const handleOpenAdd = () => {
    setEditingPetId(null);
    setNombre('');
    setTipo('Perro');
    setRaza('');
    setGenero('Macho');
    setEdad('');
    setCaracteristicas('');
    setArchivo(null);
    setPreviewUrl('');
    setModalError('');
    setShowModal(true);
  };

  // Open modal for editing
  const handleOpenEdit = (pet) => {
    setEditingPetId(pet.id);
    setNombre(pet.nombre || '');
    setTipo(pet.tipo || 'Perro');
    setRaza(pet.raza || '');
    setGenero(pet.genero || 'Macho');
    setEdad(pet.edad || '');
    setCaracteristicas(pet.caracteristicas || '');
    setArchivo(null);
    setPreviewUrl(pet.fotoUrl || '');
    setModalError('');
    setShowModal(true);
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArchivo(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Save pet (add or update)
  const handleSave = async (e) => {
    e.preventDefault();
    if (!nombre.trim()) {
      setModalError('El nombre es obligatorio.');
      return;
    }

    setGuardando(true);
    setModalError('');
    let finalFotoUrl = previewUrl;

    try {
      // 1. Upload photo if selected
      if (archivo) {
        const path = `mascotas/${currentUser.uid}/${Date.now()}_${archivo.name}`;
        const storageRef = ref(storage, path);
        const snapshot = await uploadBytes(storageRef, archivo);
        finalFotoUrl = await getDownloadURL(snapshot.ref);
      }

      const petData = {
        nombre: nombre.trim(),
        tipo,
        raza: raza.trim(),
        genero,
        edad: edad.trim(),
        caracteristicas: caracteristicas.trim(),
        fotoUrl: finalFotoUrl,
        condominioId: userData?.condominioId || '',
        usuarioId: currentUser.uid,
        unidad: userData?.numeroCasa || '',
        nombreDueño: userData?.nombre || '',
        telefonoDueño: userData?.numeroCelular || userData?.telefono || '',
        fechaRegistro: Timestamp.now()
      };

      if (editingPetId) {
        // Update
        const docRef = doc(db, 'mascotas', editingPetId);
        await updateDoc(docRef, petData);
      } else {
        // Create new
        await addDoc(collection(db, 'mascotas'), petData);
      }

      setShowModal(false);
    } catch (error) {
      console.error("Error saving pet:", error);
      setModalError('Error al guardar: ' + error.message);
    } finally {
      setGuardando(false);
    }
  };

  // Delete pet
  const handleDelete = async (petId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta mascota?')) return;
    try {
      await deleteDoc(doc(db, 'mascotas', petId));
    } catch (error) {
      console.error("Error deleting pet:", error);
      alert("Error al eliminar mascota.");
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/mascotas" />

      <main className="ks-main">
        <SafeAreaInsets top={false} bottom={true} />

        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>

        <div className="ks-scroll-area">
            {/* Header Hero Banner */}
            <div className="muro-header-stitch">
              <div className="muro-hero-card">
                <div className="muro-hero-content">
                  <h1>Mis Mascotas</h1>
                  <p>Registro y censo de tus animales</p>
                  <button className="muro-hero-action-btn" onClick={handleOpenAdd}>
                    <IoAddCircleOutline size={20} />
                    <span>Agregar</span>
                  </button>
                </div>
                <IoPawOutline className="muro-hero-icon-decor" />
              </div>
            </div>

            <div className="mascotas-container-custom">

            {/* List area */}
            <div className="mascotas-dashboard-list">
              {loading && (
                <div className="empty-state">
                  <p>Cargando tus mascotas...</p>
                </div>
              )}

              {!loading && mascotas.length === 0 && (
                <div className="empty-state" style={{ background: '#fff', borderRadius: '16px', padding: '40px 20px', border: '1px solid #e2e8f0' }}>
                  <IoPawOutline size={48} style={{ opacity: 0.3, marginBottom: 10, color: '#f97316' }} />
                  <p style={{ color: '#4b5563', fontSize: '1rem' }}>No tienes mascotas registradas.</p>
                  <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginTop: '4px' }}>Agrégalas para identificarlas fácilmente en caso de extravío.</p>
                </div>
              )}

              {!loading && mascotas.length > 0 && (
                <div className="mascotas-grid">
                  {mascotas.map(pet => (
                    <div key={pet.id} className="mascota-card">
                      {pet.fotoUrl ? (
                        <img src={pet.fotoUrl} alt={pet.nombre} className="mascota-card-img" />
                      ) : (
                        <div className="mascota-card-placeholder">
                          <IoPaw size={48} />
                        </div>
                      )}
                      
                      <div className="mascota-card-details">
                        <div className="mascota-card-title-row">
                          <h3>{pet.nombre}</h3>
                          <span className={`gender-badge ${pet.genero?.toLowerCase()}`}>
                            {pet.genero === 'Hembra' ? <IoFemale size={14} /> : <IoMale size={14} />} {pet.genero}
                          </span>
                        </div>

                        <div className="mascota-meta-info">
                          <span><strong>Tipo:</strong> {pet.tipo}</span>
                          {pet.raza && <span><strong>Raza:</strong> {pet.raza}</span>}
                          {pet.edad && <span><strong>Edad:</strong> {pet.edad}</span>}
                        </div>

                        {pet.caracteristicas && (
                          <p className="mascota-features">
                            <strong>Características:</strong> {pet.caracteristicas}
                          </p>
                        )}

                        <div className="mascota-card-actions">
                          <button 
                            className="card-action-btn edit" 
                            onClick={() => handleOpenEdit(pet)}
                            title="Editar"
                          >
                            <IoPencilOutline size={16} />
                            <span>Editar</span>
                          </button>
                          <button 
                            className="card-action-btn delete" 
                            onClick={() => handleDelete(pet.id)}
                            title="Eliminar"
                          >
                            <IoTrashOutline size={16} />
                            <span>Eliminar</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <KondoBottomNav activeRoute="/" />

      {/* --- FORM MODAL --- */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px', padding: 25, borderRadius: 20 }}>
            <button 
              className="modal-top-close-button" 
              onClick={() => setShowModal(false)}
              disabled={guardando}
            >
              <IoClose />
            </button>
            
            <h2>{editingPetId ? 'Editar Mascota' : 'Registrar Mascota'}</h2>
            
            <form onSubmit={handleSave} style={{ marginTop: '15px' }}>
              {modalError && (
                <div className="modal-error-banner">
                  <IoAlertCircleOutline size={18} />
                  <span>{modalError}</span>
                </div>
              )}

              {/* Photo Upload with Preview */}
              <div className="form-group-premium" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '15px' }}>
                <label className="form-label-premium">Foto de tu Mascota</label>
                <div className="pet-photo-picker-wrapper">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Preview" className="pet-photo-preview" />
                  ) : (
                    <div className="pet-photo-picker-placeholder">
                      <IoPaw size={40} style={{ color: '#cbd5e1' }} />
                    </div>
                  )}
                  <div className="pet-photo-upload-overlay">
                    <IoCloudUploadOutline size={20} />
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileChange} 
                      style={{ position: 'absolute', opacity: 0, inset: 0, cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group-premium">
                <label className="form-label-premium">Nombre</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={nombre} 
                  onChange={(e) => setNombre(e.target.value)} 
                  placeholder="Ej. Firulais, Max, Luna..."
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <div className="form-group-premium" style={{ flex: 1 }}>
                  <label className="form-label-premium">Tipo</label>
                  <select 
                    className="form-input" 
                    value={tipo} 
                    onChange={(e) => setTipo(e.target.value)}
                  >
                    <option value="Perro">Perro</option>
                    <option value="Gato">Gato</option>
                    <option value="Ave">Ave</option>
                    <option value="Reptil">Reptil</option>
                    <option value="Tortuga">Tortuga</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                <div className="form-group-premium" style={{ flex: 1 }}>
                  <label className="form-label-premium">Género</label>
                  <select 
                    className="form-input" 
                    value={genero} 
                    onChange={(e) => setGenero(e.target.value)}
                  >
                    <option value="Macho">Macho</option>
                    <option value="Hembra">Hembra</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <div className="form-group-premium" style={{ flex: 1 }}>
                  <label className="form-label-premium">Raza (Opcional)</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={raza} 
                    onChange={(e) => setRaza(e.target.value)} 
                    placeholder="Ej. Husky, Mestizo..."
                  />
                </div>

                <div className="form-group-premium" style={{ flex: 1 }}>
                  <label className="form-label-premium">Edad</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={edad} 
                    onChange={(e) => setEdad(e.target.value)} 
                    placeholder="Ej. 2 años, 5 meses..."
                  />
                </div>
              </div>

              <div className="form-group-premium">
                <label className="form-label-premium">Señas Particulares / Características</label>
                <textarea 
                  className="form-input" 
                  rows="2" 
                  value={caracteristicas} 
                  onChange={(e) => setCaracteristicas(e.target.value)} 
                  placeholder="Ej. Color café, tiene collar rojo, mancha negra en ojo derecho..."
                  style={{ fontFamily: 'inherit', resize: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                <button 
                  type="submit" 
                  className="action-btn-primary" 
                  style={{ backgroundColor: '#ea580c', width: '100%', justifyContent: 'center' }}
                  disabled={guardando}
                >
                  {guardando ? 'Guardando...' : 'Guardar Mascota'}
                </button>
                <button 
                  type="button" 
                  className="action-btn-secondary" 
                  style={{ backgroundColor: '#64748b', width: '100%', justifyContent: 'center' }}
                  onClick={() => setShowModal(false)}
                  disabled={guardando}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MisMascotasScreen;
